import type MockAdapter from "axios-mock-adapter"
import { mockOrganizations } from "./mock-org-service"

/**
 * Rich mocks for the load-bearing endpoints that the app hits on boot and on
 * primary navigation. These return correctly-shaped (often nested) payloads
 * that the generic fallback in mock-fallback-service.ts cannot infer.
 *
 * Everything not handled here (or in the other mock-*-service files) is caught
 * by the global fallback so the app never hard-fails while prototyping.
 */

const now = () => new Date().toISOString()
const iso = (d: Date) => d.toISOString()
const daysFromNow = (n: number) =>
  new Date(Date.now() + n * 24 * 60 * 60 * 1000)

const ORG_ID = mockOrganizations[0]?.id ?? "org-1"

// Regex helper: matches an org-scoped path with any organization id.
const orgPath = (suffix: string) =>
  new RegExp(`/organizations/[^/]+/${suffix}$`)

export function registerCoreMocks(mock: MockAdapter) {
  // ── Auth: user-info / profile ───────────────────────────────────────────
  mock.onGet("/auth/user-info").reply(200, {
    user: {
      id: "user-1",
      firstName: "John",
      lastName: "Doe",
      email: "admin@example.com",
      status: 1,
      createdAt: now(),
      lastLoginAt: now(),
      needsLegalConsent: false,
      organizationRole: "OrganizationOwner",
    },
    roles: ["OrganizationOwner", "RegisteredUser", "Admin", "SuperAdmin", "TeamMember"],
    permissions: ["read", "write"],
    currentOrganizationId: ORG_ID,
    organizations: mockOrganizations,
  })

  const userProfile = {
    id: "user-1",
    externalId: "ext-user-1",
    status: 1,
    email: "admin@example.com",
    firstName: "John",
    lastName: "Doe",
    phoneNumber: "+27821234567",
    createdAt: now(),
    lastLoginAt: now(),
    currentOrganizationId: ORG_ID,
    currentOrganizationName: mockOrganizations[0]?.name ?? "Acme Recruitment",
    roles: ["OrganizationOwner", "RegisteredUser", "Admin"],
    organizations: mockOrganizations.map((o) => ({ id: o.id, name: o.name })),
  }
  mock.onGet("/Auth/Profile").reply(200, userProfile)
  mock.onPut("/Auth/Profile").reply((config) => {
    const body = JSON.parse(config.data ?? "{}")
    return [200, { ...userProfile, ...body }]
  })

  // ── Organization profile ────────────────────────────────────────────────
  mock.onGet(orgPath("profile")).reply(() => {
    const org = mockOrganizations[0]
    return [
      200,
      {
        organizationId: org?.id ?? ORG_ID,
        organizationName: org?.name ?? "Acme Recruitment",
        userName: "John Doe",
        userId: "user-1",
        status: "Active",
        plan: String(org?.plan ?? "Recruitment"),
        description: "A mock organisation used for prototyping.",
        welcomeMessage: "Welcome aboard!",
        createdAt: now(),
        lastLoginDate: now(),
        welcomeVideoBlobUrl: null,
        vacancyCount: 6,
        employeeCount: 24,
        candidateCount: 87,
      },
    ]
  })

  // ── Users & roles ───────────────────────────────────────────────────────
  const mockRoles = [
    { id: "role-owner", name: "Owner", description: "Full access" },
    { id: "role-recruiter", name: "Recruiter", description: "Manage hiring" },
    { id: "role-viewer", name: "Viewer", description: "Read only" },
  ]
  mock.onGet(orgPath("roles")).reply(200, { roles: mockRoles })

  mock.onGet(orgPath("users")).reply(200, {
    users: [
      {
        id: "user-1",
        email: "admin@example.com",
        firstName: "John",
        lastName: "Doe",
        phoneNumber: "+27821234567",
        status: "Active",
        createdAt: now(),
        roleId: "role-owner",
        roleName: "Owner",
      },
      {
        id: "user-2",
        email: "recruiter@example.com",
        firstName: "Sarah",
        lastName: "Ndlovu",
        phoneNumber: "+27829876543",
        status: "Active",
        createdAt: now(),
        roleId: "role-recruiter",
        roleName: "Recruiter",
      },
      {
        id: "user-3",
        email: "teammate@example.com",
        firstName: "Thabo",
        lastName: "Mokoena",
        phoneNumber: "+27831112222",
        status: "Accepted",
        createdAt: now(),
        roleId: "role-team-member",
        roleName: "TeamMember",
      },
    ],
  })

  // ── Dashboard stats ─────────────────────────────────────────────────────
  mock.onGet(orgPath("dashboard/stats")).reply(200, {
    recruitment: {
      totalVacancies: 6,
      activeVacancies: 4,
      totalApplications: 87,
      applicationsByStage: [
        { stage: "Applied", count: 40 },
        { stage: "PhoneScreen", count: 18 },
        { stage: "Interview", count: 15 },
        { stage: "Offer", count: 8 },
        { stage: "Hired", count: 6 },
      ],
      upcomingConfirmedInterviews: [
        {
          interviewId: "int-1",
          candidateName: "Thabo Mokoena",
          candidateEmail: "thabo@example.com",
          vacancyTitle: "Full Stack Developer",
          interviewTitle: "Technical Interview",
          interviewType: "Video",
          startTime: iso(daysFromNow(1)),
          endTime: iso(daysFromNow(1)),
        },
      ],
      pendingCandidates: [
        {
          interviewId: "int-2",
          applicationId: "app-2",
          candidateName: "Lerato Dlamini",
          candidateEmail: "lerato@example.com",
          vacancyTitle: "Product Designer",
          slotsOffered: 3,
          slotsOfferedAt: now(),
        },
      ],
    },
    onboarding: {
      totalEmployees: 24,
      activeEmployees: 20,
      pendingEmployees: 4,
      employeesByStatus: [
        { status: "Active", count: 20 },
        { status: "Pending", count: 4 },
      ],
    },
  })

  // ── Recruitment dashboard / stats / hired ───────────────────────────────
  mock.onGet(orgPath("recruitment/dashboard")).reply(200, {
    totalActiveJobs: 4,
    totalCandidates: 87,
    totalApplications: 87,
    pendingReview: 40,
    inInterview: 15,
    offersExtended: 8,
    hiredThisMonth: 6,
    applicationsByStage: {
      Applied: 40,
      PhoneScreen: 18,
      Interview: 15,
      Offer: 8,
      Hired: 6,
    },
    topJobs: [
      {
        jobId: "job-1",
        title: "Full Stack Developer",
        applications: 32,
        newApplications: 5,
      },
      {
        jobId: "job-2",
        title: "Product Designer",
        applications: 21,
        newApplications: 2,
      },
    ],
  })

  mock.onGet(orgPath("recruitment/stats")).reply(200, {
    totalVacancies: 6,
    activeVacancies: 4,
    totalApplications: 87,
    applicationsByStage: [
      { stage: "Applied", count: 40 },
      { stage: "Interview", count: 15 },
      { stage: "Hired", count: 6 },
    ],
    upcomingConfirmedInterviews: [],
    pendingCandidates: [],
  })

  mock.onGet(/\/recruitment\/hired$/).reply(200, [])

  // ── Vacancy questions / scorecard template ──────────────────────────────
  mock.onGet(orgPath("questions")).reply(200, [])

  // ── Interviews: call quota / background checks usage ────────────────────
  mock.onGet(orgPath("interview/call-quota")).reply(200, {
    used: 3,
    limit: 50,
    remaining: 47,
    resetsAt: iso(daysFromNow(30)),
  })
  mock.onGet(orgPath("background-checks/usage")).reply(200, {
    used: 2,
    limit: 25,
    remaining: 23,
  })

  // ── Subscription ────────────────────────────────────────────────────────
  const subscriptionStatus = {
    hasSubscription: true,
    subscriptionId: "sub-active-1",
    status: "Active",
    isInGracePeriod: false,
    organizationStatus: "Active",
    currentPeriodEnd: iso(daysFromNow(30)),
    planName: "Recruitment Pro",
    price: 999,
    vatAmount: 149.85,
    totalAmount: 1148.85,
    currency: "ZAR",
    billingInterval: "Monthly",
    cancelAtPeriodEnd: false,
    isInTrial: false,
    daysUntilRenewal: 30,
  }
  mock.onGet("/subscription/status").reply(200, subscriptionStatus)
  mock.onGet("/subscription/active").reply(200, subscriptionStatus)
  mock.onGet("/api/subscription/active").reply(200, subscriptionStatus)
  mock.onGet("/subscription/pending").reply(200, null)
  mock.onGet("/subscription/history").reply(200, [])
  mock.onGet("/subscription/payment-history").reply(200, [])
  mock.onGet("/subscription/grace-period-info").reply(200, {
    isInGracePeriod: false,
    daysRemaining: 0,
    failedAttempts: 0,
    organizationActive: true,
    message: "Subscription is active and in good standing.",
  })

  // ── Wallet ──────────────────────────────────────────────────────────────
  mock.onGet("/wallet").reply(200, {
    id: "wallet-1",
    organizationId: ORG_ID,
    balance: 1250.5,
    currency: "ZAR",
    lowBalanceThreshold: 100,
    isLowBalanceNotified: false,
    createdAt: now(),
    updatedAt: now(),
  })
  mock.onGet("/wallet/transactions").reply(200, {
    items: [],
    totalCount: 0,
    page: 1,
    pageSize: 20,
  })
  mock.onGet("/wallet/ai-usage/summary").reply(200, {
    organizationId: ORG_ID,
    totalEvents: 0,
    totalInputTokens: 0,
    totalOutputTokens: 0,
    totalCostUsd: 0,
    totalLocalCurrencyCost: 0,
    currencyCode: "ZAR",
    byFeature: [],
  })
  mock.onGet("/wallet/ai-usage/events").reply(200, {
    items: [],
    totalCount: 0,
    page: 1,
    pageSize: 20,
  })

  // ── Marketplace ─────────────────────────────────────────────────────────
  mock.onGet("/marketplace/plugins").reply(200, [])
  mock.onGet(orgPath("marketplace/connectors")).reply(200, {})
  mock.onGet(orgPath("marketplace/installs")).reply(200, [])

  // ── Engage ──────────────────────────────────────────────────────────────
  mock.onGet(orgPath("engage/employees")).reply(200, [])
  mock.onGet(orgPath("engage/distribution-runs")).reply(200, [])

  // ── Integration connection statuses (default: not connected) ────────────
  mock.onGet(orgPath("careers24/connection")).reply(200, { connected: false })
  mock.onGet(orgPath("microsoft365/status")).reply(200, { connected: false })
  mock.onGet(orgPath("sage300/connection")).reply(200, { connected: false })
  mock.onGet(orgPath("sagevip/connection")).reply(200, { connected: false })

  // ── Legal documents ─────────────────────────────────────────────────────
  mock.onGet("/legal-documents").reply(200, [])
  mock.onGet(/\/legal-documents\/[^/]+$/).reply(200, {
    type: "terms",
    version: "1.0",
    content: "Mock legal document content.",
    effectiveDate: now(),
  })
}
